import {
    Box,
    Button,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { SnackbarSuccess } from "../../../components/base/SnackbarAlert/SnackbarSuccess";
import LoaderWrapper from "../../../components/wrapper/LoaderWrapper";
import { useInvestments, useUpdateInvestmentMutation } from "../../../services/productsService";
import InfoIcon from "@mui/icons-material/Info";

export function InvestmentManagementTab() {
    const { t } = useTranslation();
    const [editingRows, setEditingRows] = useState({});

    const { data: investmentsResponse, isLoading, error } = useInvestments();
    const investments = investmentsResponse?.results || [];

    const updateInvestmentMutation = useUpdateInvestmentMutation({
        onSuccess: () => {
            toast.custom(() => (
                <SnackbarSuccess
                    message={t("products.SalesInvestmentPage.investmentUpdatedSuccessfully")}
                />
            ));
            setEditingRows({});
        },
        onError: () => {
            toast.error(t("products.SalesInvestmentPage.failedToUpdateInvestment"));
        },
    });

    const handleInputChange = (investmentId, field, value) => {
        setEditingRows((prev) => {
            const hasOtherEdits = Object.keys(prev).some((id) => id !== investmentId);
            if (hasOtherEdits) {
                return {
                    [investmentId]: {
                        ...prev[investmentId],
                        id: investmentId,
                        [field]: value,
                    },
                };
            }
            return {
                ...prev,
                [investmentId]: {
                    ...prev[investmentId],
                    id: investmentId,
                    [field]: value,
                },
            };
        });
    };

    const handleSave = (investment) => {
        const editedData = editingRows[investment.id];
        if (editedData) {
            const bookingDuration =
                editedData.invest_booking_duration_days ?? investment.invest_booking_duration_days;
            const rescheduleDays =
                editedData.invest_booking_reschedule_days ??
                investment.invest_booking_reschedule_days;
            if (!bookingDuration || bookingDuration <= 0) {
                toast.error(t("products.SalesInvestmentPage.bookingDurationRequired"));
                return;
            }

            if (!rescheduleDays || rescheduleDays < 0) {
                toast.error(t("products.SalesInvestmentPage.rescheduleDaysRequired"));
                return;
            }

            updateInvestmentMutation.mutate({
                id: investment.id,
                invest_keywords: editedData.invest_keywords,
                invest_booking_duration_days: bookingDuration,
                invest_booking_reschedule_days: rescheduleDays,
            });
        }
    };

    const getCurrentValue = (investment, field) => {
        return editingRows[investment.id]?.[field] ?? investment[field];
    };

    const hasChanges = (investmentId) => {
        return editingRows[investmentId] !== undefined;
    };

    const isRowDisabled = (investmentId) => {
        const editingRowIds = Object.keys(editingRows);
        return editingRowIds.length > 0 && !editingRowIds.includes(investmentId);
    };

    if (error) {
        return (
            <Grid
                container
                justifyContent="center"
                alignItems="center"
                style={{ minHeight: "400px" }}
            >
                <Grid item>
                    <Typography variant="h6" color="error">
                        {t("products.SalesInvestmentPage.errorLoadingInvestments")}
                    </Typography>
                </Grid>
            </Grid>
        );
    }

    const tableComponent = () => (
        <TableContainer component={Paper} elevation={6}>
            <Table>
                <TableHead>
                    <TableRow sx={{ backgroundColor: "grey.100" }}>
                        <TableCell
                            sx={{ fontWeight: "bold", color: "primary.main", fontSize: "1.1rem" }}
                        >
                            {t("products.SalesInvestmentPage.investmentName")}
                        </TableCell>
                        <TableCell
                            sx={{ fontWeight: "bold", color: "primary.main", fontSize: "1.1rem" }}
                        >
                            {t("products.SalesInvestmentPage.investmentKeyWords")}
                            <Tooltip title={t("products.SalesInvestmentPage.investmentKeyWordsInfo")}>
                                 <InfoIcon fontSize="small" sx={{marginLeft: "10px"}}/>
                            </Tooltip>
                        </TableCell>
                        <TableCell
                            sx={{ fontWeight: "bold", color: "primary.main", fontSize: "1.1rem" }}
                        >
                            {t("products.SalesInvestmentPage.bookingDuration")}
                        </TableCell>
                        <TableCell
                            sx={{ fontWeight: "bold", color: "primary.main", fontSize: "1.1rem" }}
                        >
                            {t("products.SalesInvestmentPage.bookingRescheduleDays")}
                        </TableCell>
                        <TableCell
                            sx={{ fontWeight: "bold", color: "primary.main", fontSize: "1.1rem" }}
                        >
                            {t("products.SalesInvestmentPage.actions")}
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {investments?.map((investment) => (
                        <TableRow
                            key={investment.id}
                            sx={{
                                "&:hover": { backgroundColor: "grey.50" },
                                "&:nth-of-type(even)": { backgroundColor: "grey.25" },
                            }}
                        >
                            <TableCell>
                                <TextField
                                    size="medium"
                                    type="text"
                                    InputProps={{ sx: { fontSize: "1.1rem" } }}
                                    value={getCurrentValue(investment, "investment_name_sales")}
                                    onChange={(e) =>
                                        handleInputChange(
                                            investment.id,
                                            "investment_name_sales",
                                            e.target.value,
                                        )
                                    }
                                    placeholder={
                                        t("products.SalesInvestmentPage.investmentName") ?? ""
                                    }
                                    variant="outlined"
                                    fullWidth
                                    sx={{ maxWidth: 300 }}
                                    required
                                    disabled={true}
                                    error={
                                        !getCurrentValue(investment, "investment_name_sales") ||
                                        getCurrentValue(investment, "investment_name_sales")
                                            .toString()
                                            .trim() === ""
                                    }
                                />
                            </TableCell>
                            <TableCell>
                               
                                    <TextField
                                        size="medium"
                                        type="text"
                                        InputProps={{ sx: { fontSize: "1.1rem" } }}
                                        value={getCurrentValue(investment, "invest_keywords")}
                                        onChange={(e) =>
                                            handleInputChange(
                                                investment.id,
                                                "invest_keywords",
                                                e.target.value,
                                            )
                                        }
                                        placeholder={
                                            t("products.SalesInvestmentPage.investmentKeyWords") ?? ""
                                        }
                                        variant="outlined"
                                        fullWidth
                                        sx={{ maxWidth: 300 }}
                                        required
                                        disabled={isRowDisabled(investment.id)}
                                        error={null}
                                    />
                            </TableCell>
                            <TableCell>
                                <TextField
                                    size="medium"
                                    type="number"
                                    InputProps={{ sx: { fontSize: "1.1rem" } }}
                                    value={getCurrentValue(
                                        investment,
                                        "invest_booking_duration_days",
                                    )}
                                    onChange={(e) =>
                                        handleInputChange(
                                            investment.id,
                                            "invest_booking_duration_days",
                                            e.target.value,
                                        )
                                    }
                                    placeholder={t("products.SalesInvestmentPage.days") ?? ""}
                                    variant="outlined"
                                    fullWidth
                                    inputProps={{ min: 1 }}
                                    sx={{ maxWidth: 200 }}
                                    required
                                    disabled={isRowDisabled(investment.id)}
                                    error={
                                        !getCurrentValue(
                                            investment,
                                            "invest_booking_duration_days",
                                        ) ||
                                        Number(
                                            getCurrentValue(
                                                investment,
                                                "invest_booking_duration_days",
                                            ),
                                        ) <= 0
                                    }
                                />
                            </TableCell>
                            <TableCell>
                                <TextField
                                    size="medium"
                                    type="number"
                                    InputProps={{ sx: { fontSize: "1.1rem" } }}
                                    value={getCurrentValue(
                                        investment,
                                        "invest_booking_reschedule_days",
                                    )}
                                    onChange={(e) =>
                                        handleInputChange(
                                            investment.id,
                                            "invest_booking_reschedule_days",
                                            e.target.value,
                                        )
                                    }
                                    placeholder={t("products.SalesInvestmentPage.days") ?? ""}
                                    variant="outlined"
                                    fullWidth
                                    inputProps={{ min: 0 }}
                                    sx={{ maxWidth: 200 }}
                                    required
                                    disabled={isRowDisabled(investment.id)}
                                    error={
                                        getCurrentValue(
                                            investment,
                                            "invest_booking_reschedule_days",
                                        ) === "" ||
                                        getCurrentValue(
                                            investment,
                                            "invest_booking_reschedule_days",
                                        ) === null ||
                                        getCurrentValue(
                                            investment,
                                            "invest_booking_reschedule_days",
                                        ) === undefined
                                    }
                                />
                            </TableCell>
                            <TableCell>
                                <Button
                                    variant="contained"
                                    size="medium"
                                    onClick={() => handleSave(investment)}
                                    disabled={
                                        !hasChanges(investment.id) ||
                                        updateInvestmentMutation.isPending
                                    }
                                    sx={{
                                        width: 100,
                                        fontSize: "1.05rem",
                                        backgroundColor:
                                            hasChanges(investment.id) &&
                                            !updateInvestmentMutation.isPending
                                                ? "primary.main"
                                                : "grey.300",
                                        "&:hover": {
                                            backgroundColor:
                                                hasChanges(investment.id) &&
                                                !updateInvestmentMutation.isPending
                                                    ? "primary.dark"
                                                    : "grey.300",
                                        },
                                    }}
                                >
                                    {t("products.SalesInvestmentPage.save")}
                                </Button>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            {investments?.length === 0 && (
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="body1" color="grey.600" sx={{ fontSize: "1.1rem" }}>
                        {t("products.SalesInvestmentPage.noInvestmentsFound")}
                    </Typography>
                </Box>
            )}
        </TableContainer>
    );

    return (
        <Box sx={{ p: 2 }}>
            <LoaderWrapper showLoader={isLoading}>{tableComponent()}</LoaderWrapper>
        </Box>
    );
}
